import { useMemo } from "react";

import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import {
  ProductBySlugDesignerAttribute,
  ProductBySlugNonNullable,
  ProductRelatedListingPage,
} from "@/common/contracts/pdp.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { isProductBySlugDesignerAttribute } from "@/common/utils/pdp.utils";
import type { ChipLink } from "@/components/search/plp-seo/LinkChips";
import { getIconGuideLinksForProduct } from "@/components/search/plp-seo/icon-guides.utils";
import { Heading } from "@whoppah/ui/cva/Heading";

type ChipProps = {
  relatedPage?: ProductRelatedListingPage;
  designer?: ProductBySlugDesignerAttribute;
  /** WHO-6196: an icon guide link; its href already carries the locale prefix. */
  iconGuide?: ChipLink;
};

const Chip = ({ relatedPage, designer, iconGuide }: ChipProps) => {
  const handleClick = () => {
    /** Trigger `pdp_see_more` event. */
    analyticsService.trackSeeMoreClickPdp();
  };

  const url = iconGuide
    ? iconGuide.href
    : relatedPage?.url
    ? `/${relatedPage.url}`
    : designer?.slug
    ? `/designers/${designer.slug}`
    : null;

  const label = iconGuide?.title ?? relatedPage?.title ?? designer?.title;

  if (!url || !label) {
    return null;
  }

  return (
    <Link
      href={url}
      onClick={handleClick}
      className="rounded-[200px] border border-grey-500 p-2 font-title text-sm font-semibold hover:bg-grey-50"
    >
      {label}
    </Link>
  );
};

type ProductDetailsListingPagesChipsProps = {
  product: ProductBySlugNonNullable;
};

export const ProductDetailsListingPagesChips = ({
  product,
}: ProductDetailsListingPagesChipsProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const relatedListingPages = product.relatedListingPages;

  const iconGuides = useMemo(
    () =>
      getIconGuideLinksForProduct(locale, product.brand?.slug, [
        product.title,
        product.original_title,
      ]),
    [locale, product.brand?.slug, product.title, product.original_title]
  );

  const designerList = useMemo(() => {
    return product.attributes.filter(isProductBySlugDesignerAttribute);
  }, [product.attributes]);

  if (
    !iconGuides.length &&
    !relatedListingPages.length &&
    !designerList.length
  ) {
    return null;
  }

  return (
    <div className="mx-auto flex flex-col space-y-6 md:col-span-3">
      <Heading className="text-center" as="h4">
        {t("pdp:pdp-see-more")}
      </Heading>
      <div>
        <div className="-ml-4 -mt-4 flex flex-wrap justify-center space-x-4 space-y-4 [&>:first-child]:ml-4 [&>:first-child]:mt-4">
          {iconGuides.map(iconGuide => (
            <Chip key={iconGuide.href} iconGuide={iconGuide} />
          ))}
          {relatedListingPages.map(relatedPage => (
            <Chip key={relatedPage.title} relatedPage={relatedPage} />
          ))}
          {designerList?.map(designer => (
            <Chip key={designer.id} designer={designer} />
          ))}
        </div>
      </div>
    </div>
  );
};
